import React, { useState } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useAdminCategories, useUpdateCategory } from '@/hooks/useEducationAdmin';
import { CourseCard } from './components/CourseCard';
import { CourseEditDialog } from './CourseEditDialog';
import { AdminEducationCategory } from '@/hooks/useEducationAdmin';

export const CourseManagement: React.FC = () => {
  const { data: categories = [], isLoading } = useAdminCategories();
  const updateCategory = useUpdateCategory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AdminEducationCategory | null>(null);
  const [localCategories, setLocalCategories] = useState<AdminEducationCategory[]>([]);

  React.useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localCategories.findIndex((item) => item.id === active.id);
      const newIndex = localCategories.findIndex((item) => item.id === over.id);
      
      const newOrder = arrayMove(localCategories, oldIndex, newIndex);
      setLocalCategories(newOrder);

      // Update sort_order for all affected items
      const updates = newOrder.map((category, index) => 
        updateCategory.mutateAsync({ 
          id: category.id, 
          sort_order: index 
        })
      );

      try {
        await Promise.all(updates);
      } catch (error) {
        // Revert on error
        setLocalCategories(categories);
      }
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setDialogOpen(true);
  };

  const handleEdit = (category: AdminEducationCategory) => {
    setEditingCategory(category);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Course Management</h2>
          <p className="text-muted-foreground">
            Create and organize course categories. Drag and drop to reorder.
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Course
        </Button>
      </div>

      {localCategories.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No courses found. Create your first course to get started.</p>
        </div>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {localCategories.map((category) => (
                <CourseCard
                  key={category.id}
                  category={category}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <CourseEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={editingCategory}
      />
    </div>
  );
};